import { LoginComponent } from './auth/component/login/login.component';
import { LogoutComponent } from './auth/component/logout/logout.component';
import { MfaComponent } from './auth/component/mfa/mfa.component';
import { ChangePasswordComponent } from './auth/component/change-password/change-password.component';
import { ResetPasswordComponent } from './auth/component/reset-password/reset-password.component';
import { ForgotPasswordComponent } from './auth/component/forgot-password/forgot-password.component';
import { Route, Routes } from '@angular/router';
import { MainWindowComponent } from './layout/main-window/main-window.component';
import * as Guards from './guards';
import { NotFoundComponent } from './layout/not-found/not-found.component';
import { Type } from '@angular/core';
import { ENVIRONMENT } from '@env/environment';
import { isSessionFeatureEnabled, isSessionFeatureDisabled } from '@shared/utils/session-feature-utils';

const authRoutes: Routes = [
  {
    path: 'change-password',
    title: 'ROUTES.CHANGE_PASSWORD',
    component: ChangePasswordComponent,
  },
  {
    path: 'forgot-password',
    title: 'ROUTES.FORGOT_PASSWORD',
    component: ForgotPasswordComponent,
  },
  { path: 'login', title: 'ROUTES.LOGIN', component: LoginComponent },
  {
    path: 'login/:project',
    title: 'ROUTES.LOGIN',
    component: LoginComponent,
    canActivate: [Guards.LoginEntryGuard],
  },
  { path: 'logout', title: 'ROUTES.LOGOUT', component: LogoutComponent },
  { path: 'logout/:project', title: 'ROUTES.LOGOUT', component: LogoutComponent },
  { path: 'mfa', title: 'ROUTES.MFA', component: MfaComponent },
  { path: 'mfa/:project', title: 'ROUTES.MFA', component: MfaComponent },
  {
    path: 'reset-password/:user/:token',
    title: 'ROUTES.RESET_PASSWORD',
    component: ResetPasswordComponent,
  },
  // OLD PATTERN
  {
    path: 'login/reset_password/:user/:token',
    pathMatch: 'full',
    redirectTo: (route) => {
      const { user, token } = route.params;
      return `/reset-password/${user}/${token}`;
    },
  },
];
export const routes: Routes = [
  ...authRoutes,
  {
    path: '',
    component: MainWindowComponent,
    children: [
      {
        path: '',
        redirectTo: 'dashboard',
        pathMatch: 'full',
      },
      {
        path: 'dashboard',
        title: 'ROUTES.DASHBOARD',
        loadChildren: () => import('./views/dashboard/routes').then((component) => component.ROUTES),
        canActivate: [Guards.DashboardViewGuard],
      },
      {
        path: 'sessions',
        title: 'ROUTES.SESSIONS',
        loadChildren: () => import('./views/sessions/routes').then((component) => component.ROUTES),
        canActivate: [Guards.SessionsViewGuard],
      },
      {
        path: 'session/:uuid',
        loadChildren: () => import('./views/sessions/routes').then((component) => component.ROUTES),
        canActivate: [Guards.SessionsViewGuard],
      },
      {
        path: 'session',
        redirectTo: 'sessions',
      },
      {
        path: 'companies',
        title: 'ROUTES.ORGANISATIONS',
        loadChildren: () => import('./views/organisations/routes').then((component) => component.ROUTES_ORGANISATIONS),
        canActivate: [Guards.OrganisationsViewGuard],
      },
      {
        path: 'companies/new',
        loadChildren: () =>
          import('./views/organisations/routes').then((component) => component.ROUTES_ORGANISATION_ITEM),
        canActivate: [Guards.OrganisationCreateViewGuard],
      },
      // OLD URL format
      {
        path: 'company/new',
        redirectTo: 'companies/new',
      },
      {
        path: 'companies/:uuid',
        loadChildren: () =>
          import('./views/organisations/routes').then((component) => component.ROUTES_ORGANISATION_ITEM),
        canActivate: [Guards.OrganisationViewGuard],
      },
      // OLD URL format
      {
        path: 'company/:uuid',
        redirectTo: 'companies/:uuid',
      },

      {
        path: 'users',
        title: 'ROUTES.USERS',
        loadChildren: () => import('./views/users/routes').then((component) => component.ROUTES),
        canActivate: [Guards.UsersViewGuard],
      },
      {
        path: 'users/new',
        loadChildren: () => import('./views/users/profile-routes').then((component) => component.ROUTES),
        canActivate: [Guards.UsersProfileViewGuard],
      },
      {
        path: 'users/new_service_user',
        loadChildren: () => import('./views/users/profile-routes').then((component) => component.ROUTES),
        canActivate: [Guards.UsersProfileViewGuard],
      },
      {
        path: 'users/:uuid',
        loadChildren: () => import('./views/users/profile-routes').then((component) => component.ROUTES),
        canActivate: [Guards.UsersProfileViewGuard],
      },
      // OLD URL format
      {
        path: 'user/new',
        redirectTo: 'users/new',
      },
      {
        path: 'user/new_service_user',
        redirectTo: 'users/new_service_user',
      },
      {
        path: 'user/:uuid',
        redirectTo: 'users/:uuid',
      },
      {
        path: 'projects',
        title: 'ROUTES.PROJECTS',
        loadChildren: () => import('./views/projects/routes').then((component) => component.ROUTES),
        canActivate: [Guards.ProjectsViewGuard],
      },
      {
        path: 'integrations',
        title: 'ROUTES.INTEGRATIONS',
        loadChildren: () => import('./views/integrations/routes').then((component) => component.ROUTES),
        canActivate: [Guards.IntegrationsGuard],
      },
      {
        path: 'analytics',
        title: 'ROUTES.ANALYTICS',
        loadChildren: () => import('./views/analytics/routes').then((component) => component.ROUTES),
        canActivate: [Guards.AnalyticsViewGuard],
      },
      {
        path: 'templates',
        title: 'ROUTES.TEMPLATES',
        loadChildren: () => import('./views/templates/routes').then((component) => component.ROUTES),
        canActivate: [Guards.TemplatesViewGuard],
      },
      {
        path: 'templates/:uuid',
        loadChildren: () => import('./views/templates/template-routes').then((component) => component.ROUTES),
        canActivate: [Guards.TemplateDetailsViewGuard],
      },
      // OLD URL format
      {
        path: 'template/:uuid',
        redirectTo: (route) => {
          const { uuid } = route.params;
          const queryParams = new URLSearchParams();
          Object.keys(route.queryParams).forEach((key) => {
            queryParams.append(key, route.queryParams[key]);
          });
          const queryString = queryParams.toString();
          return `/templates/${uuid}${queryString ? '?' + queryString : ''}`;
        },
      },
      // Redirect /template and /template/ to /templates
      {
        path: 'template',
        redirectTo: 'templates',
        pathMatch: 'full',
      },
      {
        path: 'create_template',
        title: 'ROUTES.TEMPLATES',
        loadChildren: () => import('./views/templates/template-create-routes').then((component) => component.ROUTES),
        canActivate: [Guards.TemplateCreateViewGuard],
      },
      // Redirect NEW URL format to OLD for compatibility
      {
        path: 'create-template',
        redirectTo: 'create_template',
      },
      {
        path: 'upload_template',
        loadChildren: () => import('./views/templates/template-upload-routes').then((component) => component.ROUTES),
        canActivate: [Guards.TemplateUploadViewGuard],
      },
      // Redirect NEW URL format to OLD for compatibility
      {
        path: 'upload-template',
        redirectTo: 'upload_template',
      },

      {
        path: 'workflows2',
        title: 'ROUTES.WORKFLOWS2',
        loadChildren: () => import('./views/workflows/routes').then((component) => component.ROUTES),
        canActivate: [Guards.ProcessesViewGuard],
      },
      {
        path: 'workflow2/:uuid',
        title: 'ROUTES.WORKFLOW2_DETAILS',
        loadChildren: () => import('./views/workflows/routes').then((component) => component.ROUTES_DETAILS),
        canActivate: [Guards.ProcessDetailsViewGuard],
      },

      {
        path: 'processes/:workflowId',
        title: 'ROUTES.PROCESSES',
        loadChildren: () => import('./views/processes/routes').then((component) => component.ROUTES),
        canActivate: [Guards.ProcessesListViewGuard],
      },
      {
        path: 'workflow/:uuid', // is_autogenerated process (from template)
        title: 'ROUTES.PROCESSES',
        loadChildren: () => import('./views/processes/routes').then((component) => component.ROUTES_DETAILS_TABLE),
        canActivate: [Guards.ProcessesListViewGuard],
      },
      {
        path: 'process/:uuid',
        title: 'ROUTES.PROCESS_DETAILS',
        loadChildren: () => import('./views/processes/routes').then((component) => component.ROUTES_DETAILS),
        canActivate: [Guards.ProcessViewGuard],
      },

      {
        path: 'post-transactions',
        title: 'ROUTES.POST_TRANSACTIONS',
        loadChildren: () => import('./views/transactions/routes').then((component) => component.ROUTES),
        canActivate: [Guards.TransactionsViewGuard],
      },
      {
        path: 'post_transactions',
        redirectTo: 'post-transactions',
      },
      {
        path: 'tariffs',
        title: 'ROUTES.TARIFFS',
        loadChildren: () => import('./views/tariffs/tariff-routes').then((component) => component.ROUTES_TARIFF),
        canActivate: [Guards.TariffsViewGuard],
      },
      {
        path: 'tarifs',
        redirectTo: 'tariffs',
      },
      {
        path: 'tariffs/:uuid',
        loadChildren: () => import('./views/tariffs/tariff-routes').then((component) => component.ROUTES_TARIFF_ITEM),
        canActivate: [Guards.TariffDetailsViewGuard],
      },
      // OLD URL format
      {
        path: 'tarif/:uuid',
        redirectTo: 'tariffs/:uuid',
      },
      {
        path: 'audit-logs',
        title: 'ROUTES.AUDIT-LOGS',
        loadChildren: () => import('./views/audit-logs/routes').then((component) => component.ROUTES),
        canActivate: [Guards.LogsViewGuard],
      },
      {
        path: 'auditlog',
        redirectTo: 'audit-logs',
      },

      {
        path: 'dictionaries',
        title: 'ROUTES.DICTIONARIES',
        loadChildren: () => import('./views/dictionaries/routes').then((component) => component.ROUTES),
        canActivate: [Guards.DictionariesViewGuard],
        // component: DictListViewComponent,
      },
      {
        path: 'dictionaries/:uuid',
        title: 'ROUTES.DICTIONARIES',
        canActivate: [Guards.DictionaryDetailsViewGuard],
        loadChildren: () => import('./views/dictionaries/routes').then((component) => component.ROUTES_ITEM),
        // component: DictItemComponent,
      },
      // OLD dictionary (create page)
      {
        path: 'dictionary',
        title: 'ROUTES.DICTIONARIES',
        loadChildren: () => import('./views/dictionaries/routes').then((component) => component.ROUTES),
        canActivate: [Guards.DictionaryCreateViewGuard],
      },
      // OLD dictionary (details page - redirect to new URL format)
      {
        path: 'dictionary/:uuid',
        redirectTo: 'dictionaries/:uuid',
      },

      {
        path: 'categories',
        title: 'ROUTES.CATEGORIES',
        loadChildren: () => import('./views/categories/routes').then((component) => component.ROUTES),
        canActivate: [Guards.CategoriesViewGuard],
      },
      {
        path: 'texts-tables',
        title: 'ROUTES.DATATABLES',
        loadChildren: () => import('./views/datatables/routes').then((component) => component.ROUTES),
        canActivate: [Guards.DatatablesViewGuard],
        // component: DataTableListComponent,
      },
      {
        path: 'texts-tables-editor/:uuid',
        loadChildren: () => import('./views/datatables/routes').then((component) => component.DATA_TABLE_ROUTES),
        canActivate: [Guards.DatatableDetailsViewGuard],
      },
      // OLD URL format
      {
        path: 'texts-table',
        redirectTo: 'texts-tables',
      },
      {
        path: 'texts-table-editor/:uuid',
        redirectTo: 'texts-tables-editor/:uuid',
      },

      {
        path: 'service-call',
        component: MainWindowComponent,
        canActivate: [Guards.SupportViewGuard],
      },
    ],
    canActivate: [Guards.AuthViewGuard],
  },
  // { path: 'timeout', component: TimeoutComponent }, // TODO: Not used currently, created for feature use
  { path: '**', component: NotFoundComponent },
];

export function isPageEnabled(page: string): boolean {
  // Check if explicitly disabled in session (takes precedence)
  if (isSessionFeatureDisabled(page)) {
    return false;
  }

  const enabledPages = ENVIRONMENT.ENABLED_PAGES || [];
  if (enabledPages.includes(page)) return true;
  return isSessionFeatureEnabled(page);
}

export function buildIframeRoute(legacyPath: string, title: string): Route[] {
  return [
    {
      path: '',
      title: title,
      data: { src: `/legacy-app/${legacyPath}` },
      loadComponent: () =>
        import('./layout/iframe/iframe.component')
          .then((component) => component.IframeComponent)
          .catch((err) => {
            console.error(' >>> Failed to load iframe fallback component:', err);
            throw err;
          }),
    },
  ];
}

export function buildDefaultRoute(
  _route: string,
  title: string,
  loadComponentFn: () => Promise<{ DefaultComponent: Type<unknown> }>,
): Route {
  return {
    path: '',
    title,
    loadComponent: () => loadComponentFn().then((module) => module.DefaultComponent),
  };
}

export function buildConditionalRoute(
  key: string,
  loadComponentFn: () => Promise<{ DefaultComponent: Type<unknown> }>,
  title: string | undefined = undefined,
): Routes {
  // this function is checking ENABLED_PAGES from env
  console.log(`buildConditionalRoute : key ${key}`);
  title = `ROUTES.${(title ? title : key).toUpperCase()}`;
  return isPageEnabled(key) ? [buildDefaultRoute(key, title, loadComponentFn)] : buildIframeRoute(key, title);
}
